/**
 * PORTAL COMPONENT - Renders in Mapbox GL popup portals outside React tree
 *
 * ⚠️  DO NOT use hooks that require context (useNavigate, useLocation, useContext, etc.)
 * ⚠️  All dependencies must be passed via props from the parent component
 *
 * Safe: Pure computation, useRef, useState, useMemo (no context dependencies)
 * Unsafe: useNavigate, useLocation, useContext, custom hooks using context
 */
import { motion } from 'framer-motion';
import type { Unit } from '@/types';
import { UnitPreviewCard } from '@/components/unit/UnitPreviewCard';
import { ShareButton } from './shared/ShareButton';

interface UnitHoverCardProps {
  unit: Unit;
  /** @deprecated No longer used - navigation handles state updates via URL sync */
  onClick?: () => void;
  /** Navigation function - required since this component renders in Mapbox popup portals outside Router context */
  onNavigate: (path: string) => void;
  /** Mobile state - passed from parent since portal components should avoid hooks */
  isMobile: boolean;
  getShareableUrl?: () => string;
}

// Helper to generate semantic slug
const generateSlug = (unit: Unit) => {
  return unit.unit_code;
};

export function UnitHoverCard({ unit, onClick: _onClick, onNavigate, isMobile, getShareableUrl }: UnitHoverCardProps) {

  // Handle click on card or specific metric
  // IMPORTANT: Only use onNavigate() - do NOT call onClick() as it causes a race condition
  // between React Router URL update and the URL-to-state sync effect in Map.tsx.
  // The navigation will trigger the URL sync effect which handles state updates properly.
  const handleAction = (action: 'view' | 'solar' | 'charger' | 'hvac' | 'starlink') => {
    const slug = generateSlug(unit);
    const tabParam = action !== 'view' ? `&tab=${action}` : '';
    onNavigate(`/map?unit=${slug}&view=detail${tabParam}`);
    // Don't call onClick() here - it races with URL sync and causes detail view to close immediately
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.05 }}
      className="w-[375px] max-w-[calc(100vw-32px)] relative group"
    >
      <ShareButton
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        getShareUrl={getShareableUrl}
      />
      <UnitPreviewCard
        unit={unit}
        isMobile={isMobile}
        onAction={handleAction}
        imageHeight="h-[200px]"
        className="border border-border rounded-lg hover:border-primary/50 transition-colors cursor-pointer h-auto"
      />
    </motion.div>
  );
}
